// Coordenador cross-tab para a impressora térmica WebUSB.
//
// Por que existe: o navegador só permite UMA aba (na mesma máquina) com
// claim ativo no mesmo dispositivo USB. Quando outra aba já é dona, o
// claimInterface() falha com "Unable to claim interface" — mas a mensagem
// nativa não diz se quem está segurando é outra aba do nosso próprio app
// ou um programa externo (spooler, driver do fabricante, etc.).
//
// Esse módulo usa BroadcastChannel pra abas do mesmo origin se anunciarem
// como "donas" da impressora. Não substitui a checagem real do WebUSB —
// é só um sinalizador best-effort para diferenciar o erro pro lojista.

const CHANNEL_NAME = "vnz-thermal-printer";
const PING_TIMEOUT_MS = 200;

type Msg =
  | { type: "ping"; from: string }
  | { type: "owned"; from: string }
  | { type: "released"; from: string };

const tabId =
  typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);

let channel: BroadcastChannel | null = null;
let isOwner = false;

function getChannel(): BroadcastChannel | null {
  if (channel !== null) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.addEventListener("message", (ev) => {
      const msg = ev.data as Msg | undefined;
      if (!msg || msg.from === tabId) return;
      // Se alguém pingou e nós somos donos, anuncia.
      if (msg.type === "ping" && isOwner) {
        try {
          channel?.postMessage({ type: "owned", from: tabId } satisfies Msg);
        } catch {}
      }
    });
  } catch {
    channel = null;
  }
  return channel;
}

export function getPrinterTabId(): string {
  return tabId;
}

// Pergunta às outras abas se alguma é dona da impressora.
// Resolve com a tabId da dona, ou null se ninguém respondeu dentro do
// timeout. Sempre resolve — nunca rejeita — pra não quebrar o fluxo de
// connect caso o BroadcastChannel não esteja disponível.
export function findCurrentOwner(): Promise<string | null> {
  const ch = getChannel();
  if (!ch) return Promise.resolve(null);
  return new Promise((resolve) => {
    let owner: string | null = null;
    const onMsg = (ev: MessageEvent) => {
      const msg = ev.data as Msg | undefined;
      if (msg && msg.type === "owned" && msg.from !== tabId) {
        owner = msg.from;
      }
    };
    ch.addEventListener("message", onMsg);
    try {
      ch.postMessage({ type: "ping", from: tabId } satisfies Msg);
    } catch {}
    setTimeout(() => {
      try {
        ch.removeEventListener("message", onMsg);
      } catch {}
      resolve(owner);
    }, PING_TIMEOUT_MS);
  });
}

// Marca esta aba como dona da impressora e avisa as outras.
export function markOwner(): void {
  isOwner = true;
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "owned", from: tabId } satisfies Msg);
  } catch {}
}

// Libera a posse e avisa as outras abas.
export function markReleased(): void {
  if (!isOwner) return;
  isOwner = false;
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage({ type: "released", from: tabId } satisfies Msg);
  } catch {}
}

export function isCurrentTabOwner(): boolean {
  return isOwner;
}
